import { useState, useEffect, useCallback } from "react";
import { reminderApi } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Mail,
  Server,
  Save,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  Eye,
  EyeOff,
} from "lucide-react";

export function SmtpSettings({ toast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);

  const [formData, setFormData] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_from: "",
    reminder_email: ""
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await reminderApi.getSmtpSettings();
      const data = response.data;
      setSmtpConfigured(data.configured);
      setFormData({
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || "",
        smtp_password: "",
        smtp_from: data.smtp_from || "",
        reminder_email: data.reminder_email || ""
      });
    } catch (error) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await reminderApi.updateSmtpSettings(formData);
      toast.success("Einstellungen gespeichert");
      setSmtpConfigured(true);
      setFormData(prev => ({ ...prev, smtp_password: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await reminderApi.testSmtpSettings();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>E-Mail (SMTP)</CardTitle>
                <CardDescription>
                  Konfigurieren Sie den Exchange-Server für E-Mail-Erinnerungen
                </CardDescription>
              </div>
            </div>
            <Badge variant={smtpConfigured ? "default" : "secondary"} className="gap-1">
              {smtpConfigured ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Konfiguriert
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Nicht konfiguriert
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Server Settings */}
          <div>
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Server className="w-4 h-4" />
              Server-Einstellungen
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP Server *</Label>
                <Input
                  id="smtp_host"
                  value={formData.smtp_host}
                  onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                  placeholder="z.B. mail.r-network.de oder smtp.office365.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">Port *</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) || 587 })}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
                <p className="text-xs text-muted-foreground">Standard: 587 (TLS) oder 465 (SSL)</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Authentication */}
          <div>
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Authentifizierung
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="smtp_user">Benutzername *</Label>
                <Input
                  id="smtp_user"
                  value={formData.smtp_user}
                  onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                  placeholder="service@r-network.de"
                  data-testid="smtp-user-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_password">Passwort {smtpConfigured && "(leer = unverändert)"}</Label>
                <div className="relative">
                  <Input
                    id="smtp_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.smtp_password}
                    onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                    placeholder={smtpConfigured ? "••••••••" : "Passwort eingeben"}
                    className="pr-10"
                    data-testid="smtp-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Email Addresses */}
          <div>
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              E-Mail-Adressen
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="smtp_from">Absender-Adresse *</Label>
                <Input
                  id="smtp_from"
                  type="email"
                  value={formData.smtp_from}
                  onChange={(e) => setFormData({ ...formData, smtp_from: e.target.value })}
                  placeholder="service@r-network.de"
                  data-testid="smtp-from-input"
                />
                <p className="text-xs text-muted-foreground">Von dieser Adresse werden E-Mails gesendet</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reminder_email">Erinnerungs-Empfänger *</Label>
                <Input
                  id="reminder_email"
                  type="email"
                  value={formData.reminder_email}
                  onChange={(e) => setFormData({ ...formData, reminder_email: e.target.value })}
                  placeholder="service@r-network.de"
                  data-testid="reminder-email-input"
                />
                <p className="text-xs text-muted-foreground">An diese Adresse werden Erinnerungen gesendet</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button type="submit" disabled={saving} data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Einstellungen speichern
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleTest} 
              disabled={testing || !smtpConfigured}
              data-testid="test-smtp-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Test-E-Mail senden
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
